import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Smartphone, QrCode, Send, CheckCircle, Clock, Zap } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NfcTransfer = ({ userType = 'customer' }) => {
  const [mode, setMode] = useState('send'); // 'send' or 'receive'
  const [amount, setAmount] = useState('');
  const [receiverData, setReceiverData] = useState('');
  const [loading, setLoading] = useState(false);
  const [transferToken, setTransferToken] = useState(null);
  const [transferResult, setTransferResult] = useState(null);

  const token = localStorage.getItem('mojo_token');

  const handleGenerateToken = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/nfc/wallet/generate-token`,
        { amount: parseFloat(amount) },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { amount: parseFloat(amount) }
        }
      );

      setTransferToken(response.data);
      toast.success('Transfer token generated! Show QR code or use NFC');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate token');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemToken = async () => {
    if (!receiverData.trim()) {
      toast.error('Please enter the transfer token data');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/nfc/wallet/redeem-token`,
        { nfc_data: receiverData },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { nfc_data: receiverData }
        }
      );

      setTransferResult(response.data);
      toast.success('Transfer successful!');
      setReceiverData('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem token');
    } finally {
      setLoading(false);
    }
  };

  const handleClearSendMode = () => {
    setAmount('');
    setTransferToken(null);
  };

  const handleClearReceiveMode = () => {
    setReceiverData('');
    setTransferResult(null);
  };

  return (
    <div className="space-y-6">
      {/* Mode Selection */}
      <Card className="border-[#F2B10C]">
        <CardHeader className="bg-gradient-to-r from-[#145539] to-[#1e7049] text-white">
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            NFC Wallet Transfer
          </CardTitle>
          <CardDescription className="text-white/90">
            FREE instant wallet-to-wallet transfers via NFC or QR code
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex gap-3 mb-6">
            <Button
              onClick={() => {
                setMode('send');
                setTransferResult(null);
              }}
              variant={mode === 'send' ? 'default' : 'outline'}
              className={`flex-1 ${
                mode === 'send'
                  ? 'bg-gradient-to-r from-[#F2B10C] to-[#d99a0a]'
                  : ''
              }`}
            >
              <Send className="w-4 h-4 mr-2" />
              Send Money
            </Button>
            <Button
              onClick={() => {
                setMode('receive');
                setTransferToken(null);
              }}
              variant={mode === 'receive' ? 'default' : 'outline'}
              className={`flex-1 ${
                mode === 'receive'
                  ? 'bg-gradient-to-r from-[#145539] to-[#1e7049]'
                  : ''
              }`}
            >
              <QrCode className="w-4 h-4 mr-2" />
              Receive Money
            </Button>
          </div>

          {/* Send Mode */}
          {mode === 'send' && !transferToken && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="nfc-amount">Amount to Send (R)</Label>
                <Input
                  id="nfc-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1"
                  data-testid="nfc-send-amount-input"
                />
              </div>
              <Button
                onClick={handleGenerateToken}
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#F2B10C] to-[#d99a0a] hover:from-[#d99a0a] hover:to-[#F2B10C]"
                data-testid="nfc-generate-token-btn"
              >
                {loading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Smartphone className="w-4 h-4 mr-2" />
                    Generate Transfer Token
                  </>
                )}
              </Button>
              <p className="text-sm text-gray-500 text-center">
                Generate a secure token for the receiver to scan
              </p>
            </div>
          )}

          {/* Send Mode - Token Generated */}
          {mode === 'send' && transferToken && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-green-800">
                    Transfer Token Generated
                  </h3>
                </div>
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>Amount:</strong> R{transferToken.amount.toFixed(2)}
                  </p>
                  <p>
                    <strong>Token ID:</strong> {transferToken.token_id}
                  </p>
                  <p className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <strong>Expires in:</strong> {Math.floor(transferToken.expires_in / 60)} minutes
                  </p>
                </div>
              </div>

              {/* QR Code Display */}
              <div className="flex flex-col items-center justify-center p-6 bg-white border-2 border-dashed border-[#F2B10C] rounded-lg">
                <QRCodeSVG
                  value={transferToken.qr_data}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
                <p className="mt-4 text-sm text-gray-600 text-center">
                  Receiver scans this QR code to receive R{transferToken.amount.toFixed(2)}
                </p>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">How to complete transfer:</h4>
                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                  <li>Show this QR code to the receiver</li>
                  <li>Receiver scans the code in their "Receive Money" tab</li>
                  <li>Token is valid for 5 minutes</li>
                  <li>Transfer is FREE - no fees!</li>
                </ul>
              </div>

              <Button
                onClick={handleClearSendMode}
                variant="outline"
                className="w-full"
                data-testid="nfc-clear-send-btn"
              >
                Send Another Transfer
              </Button>
            </div>
          )}

          {/* Receive Mode */}
          {mode === 'receive' && !transferResult && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="nfc-receiver-data">Transfer Token Data</Label>
                <textarea
                  id="nfc-receiver-data"
                  placeholder='Paste the token data here or scan QR code (format: {"type":"mojo_wallet_transfer",...})'
                  value={receiverData}
                  onChange={(e) => setReceiverData(e.target.value)}
                  className="mt-1 w-full min-h-[100px] p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#F2B10C] focus:border-[#F2B10C]"
                  data-testid="nfc-receive-data-input"
                />
              </div>
              <Button
                onClick={handleRedeemToken}
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#145539] to-[#1e7049] hover:from-[#1e7049] hover:to-[#145539]"
                data-testid="nfc-redeem-token-btn"
              >
                {loading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <QrCode className="w-4 h-4 mr-2" />
                    Receive Money
                  </>
                )}
              </Button>
              <p className="text-sm text-gray-500 text-center">
                Scan the sender's QR code or paste the token data above
              </p>
            </div>
          )}

          {/* Receive Mode - Transfer Complete */}
          {mode === 'receive' && transferResult && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-semibold text-green-800">
                    Transfer Successful!
                  </h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-gray-600">Amount Received:</span>
                    <span className="text-xl font-bold text-green-600">
                      R{transferResult.amount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-gray-600">Transfer Fee:</span>
                    <span className="font-semibold text-green-600">FREE (R0.00)</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-gray-600">From:</span>
                    <span className="font-semibold">{transferResult.sender.name}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="text-gray-600">To:</span>
                    <span className="font-semibold">{transferResult.receiver.name}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600">New Balance:</span>
                    <span className="text-lg font-bold text-[#145539]">
                      R{transferResult.receiver.new_balance.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Transaction ID:</strong> {transferResult.transaction_id}
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Save this for your records
                </p>
              </div>

              <Button
                onClick={handleClearReceiveMode}
                variant="outline"
                className="w-full"
                data-testid="nfc-clear-receive-btn"
              >
                Receive Another Transfer
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NfcTransfer;
